import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { selectCustomerNumber, setCurrentCustomerNumber } from './redux/customer';
import { clearChildCustomer } from './redux/childCustomer';
import './App.css';
import globalTheme from './themes/theme';
import { LiveChatWidget } from '@livechat/widget-react';
import { RouterProvider, useLocation, createBrowserRouter, Outlet, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import LinearProgress from '@mui/material/LinearProgress';
import { usePromiseTracker } from 'react-promise-tracker';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
// @ts-ignore
import * as icons from './icons/fontawesome';
// @ts-ignore
import Navbar from './components/Navbar';
import PracticeSidebar from './components/PracticeSidebar';
import CorporateSidebar from './components/CorporateSideBar';
import AlertDisplay from './components/AlertDisplay';

// @ts-ignore
import Footer from './components/Footer';
import constants from './constants';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
// @ts-ignore
import practiceRoutes from './routes/practice';
// @ts-ignore
import corporateRoutes from './routes/corporate';
import { useAppSelector } from './redux/store';
import { selectCurrentUser } from './redux/user';
import { useSession } from './hooks/useSession';
import { SessionLoadingProvider, useSessionLoading } from './contexts/SessionLoadingContext';
import LoadingScreen from './components/LoadingScreen';

import pdfRoutes from './routes/pdf';
import AuthService from './services/authorization';
import ErrorPage from './views/common/ErrorPage';
import DashboardRedirect from './components/DashboardRedirect';
import commonRoutes from './routes/common';

library.add(fab, far, icons);

const queryClient = new QueryClient();

function SidebarWrapper() {
  const location = useLocation();
  return (
    <>
      {location.pathname.includes('/practice') && !location.pathname.includes('/corp') && <PracticeSidebar />}
      {location.pathname.includes('/corp') && <CorporateSidebar />}
    </>
  );
}

const LayoutWrapper = () => {
  const { promiseInProgress } = usePromiseTracker();

  let { customerNumber: routeCustomerNumber } = useParams();
  const dispatch = useDispatch();
  const storeCustomerNumber = useAppSelector(selectCustomerNumber);

  useEffect(() => {
    if (routeCustomerNumber) {
      dispatch(setCurrentCustomerNumber(routeCustomerNumber));
    } else if (storeCustomerNumber) {
      dispatch(clearChildCustomer());
      dispatch(setCurrentCustomerNumber(storeCustomerNumber));
    }
  }, [routeCustomerNumber, storeCustomerNumber]);

  return (
    <div className="App">
      <Navbar></Navbar>
      <div className="content-container">
        {/* Sidebar rendering based on route url */}
        <SidebarWrapper />
        <div className="body-header-container">
          <div>
            {promiseInProgress && <LinearProgress sx={{ width: '100%', position: 'fixed', top: '50px', left: '0', right: '0', zIndex: '1000' }} />}
            <Outlet />
          </div>
          <Footer></Footer>
        </div>
      </div>
      <AlertDisplay position="top-right" maxMessages={5} autoDismiss={true} />
    </div>
  );
};

const AppContent = () => {
  const { isSessionLoading } = useSessionLoading();

  if (isSessionLoading) {
    return <LoadingScreen />;
  }

  return <LayoutWrapper />;
};

const CompleteTaskRedirect = () => {
  const Auth = new AuthService();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerNumber = searchParams.get('customerNumber');
  const taskId = searchParams.get('taskId');
  const { createSession } = useSession();

  const setSession = async () => {
    if (customerNumber && Auth.getCurrentCustomerNumber() !== customerNumber) {
      Auth.setCurrentCustomerNumber(customerNumber);
    }
    await createSession();
    navigate(`/practice/tasks?taskId=${taskId}`);
  };

  useEffect(() => {
    if (customerNumber && taskId) {
      setSession();
    }
  }, [customerNumber, taskId]);

  return null;
};

const router = createBrowserRouter([
  ...commonRoutes,
  ...pdfRoutes,
  {
    path: '/',
    element: <AppContent />,
    children: [
      {
        index: true,
        element: <DashboardRedirect />
      },
      ...practiceRoutes,
      ...corporateRoutes,
      {
        path: '/complete-task',
        element: <CompleteTaskRedirect />
      }
    ]
  },
  // Catch-all route for 404 and other errors
  {
    path: '*',
    element: <ErrorPage />
  }
]);

function App() {
  return (
    <SessionLoadingProvider>
      <AppInitializer />
    </SessionLoadingProvider>
  );
}

function AppInitializer() {
  const { initializeSession } = useSession();
  const user = useAppSelector(selectCurrentUser);
  const liveChatCustomerName = user?.firstname?.trim() || undefined;

  useEffect(() => {
    const publicRoutes = ['/login', '/verify-user', '/support', '/faq', '/terms', '/error', '/signup', '/logout'];
    if (!publicRoutes.includes(window.location.pathname)) {
      initializeSession();
    }
  }, []);

  const showChatWidget = [ 'test', 'production' ].includes(constants.ENV)
  const includeReactDevTools = [ 'development', 'test' ].includes(constants.ENV)

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={globalTheme}>
        <RouterProvider router={router} />
      </ThemeProvider>
      {
        showChatWidget && (
          <LiveChatWidget
            license={constants.LIVE_CHAT_LICENSE}
            group={constants.LIVE_CHAT_GROUP}
            customerName={liveChatCustomerName}
          />
        )
      }
      {
        includeReactDevTools && (
          <ReactQueryDevtools initialIsOpen={false} />
        )
      }
    </QueryClientProvider>
  );
}

export default App;
